import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';

import { appendBelowLinkPrompt, linkPromptKey, replaceLinkPrompt } from '../LinkPrompt/LinkPrompt';
import { LinkEmbedView } from './views/LinkEmbedView';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    linkEmbed: {
      convertLinkPromptToEmbed: () => ReturnType;
      appendLinkPromptEmbed: () => ReturnType;
    };
  }
}

export const LinkEmbed = Node.create({
  name: 'linkEmbed',

  group: 'block',
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      service: {
        default: 'youtube',
        parseHTML: (element) => element.getAttribute('data-service') ?? 'youtube',
        renderHTML: (attributes) => ({ 'data-service': attributes.service }),
      },
      // Platform-specific payload (e.g. { videoId } for youtube), opaque to this generic node.
      data: {
        default: null,
        parseHTML: (element) => {
          const raw = element.getAttribute('data-embed');
          if (!raw) return null;
          try {
            return JSON.parse(raw);
          } catch {
            return null;
          }
        },
        renderHTML: (attributes) => ({
          'data-embed': attributes.data ? JSON.stringify(attributes.data) : null,
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: `div[data-type="${this.name}"]` }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': this.name })];
  },

  addCommands() {
    return {
      // "Preview only" on an embed prompt: replace the pasted link text with the player.
      convertLinkPromptToEmbed: () => (props) => {
        const prompt = linkPromptKey.getState(props.state);
        if (prompt?.kind !== 'embed') return false;
        return replaceLinkPrompt(props, prompt, {
          type: this.name,
          attrs: { service: prompt.service, data: prompt.data },
        });
      },
      // "Preview + URL" on an embed prompt: keep the link text and add the player after the block.
      appendLinkPromptEmbed: () => (props) => {
        const prompt = linkPromptKey.getState(props.state);
        if (prompt?.kind !== 'embed') return false;
        return appendBelowLinkPrompt(props, prompt, this.type.create({ service: prompt.service, data: prompt.data }));
      },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(LinkEmbedView);
  },
});

export default LinkEmbed;
